// WebSocket client for invalidate signals (Web App)
// Purpose: Listen for invalidate events from server and trigger refetch
// Architecture: WebSocket = intent only, never mutate state directly

// Lazy-load socket.io-client to avoid build-time errors
// This will be dynamically imported when connectWebSocket is called
let ioFactory = null

let socket = null
let isConnecting = false
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 5

/**
 * Connect to WebSocket server
 * @param {Function} onInvalidate - Callback when invalidate signal is received
 * @param {string} userId - User ID for authentication
 * @param {string} token - Auth token for authentication
 */
export async function connectWebSocket (onInvalidate, userId, token) {
  // Lazy-load socket.io-client only when needed
  if (!ioFactory) {
    try {
      // Dynamic import to avoid build-time resolution
      const socketIOModule = await import('socket.io-client')
      ioFactory = socketIOModule.io || socketIOModule.default || socketIOModule
      console.log('✅ [WS] socket.io-client loaded successfully')
    } catch (error) {
      console.warn('⚠️ [WS] socket.io-client not installed. WebSocket features disabled. Install with: yarn add socket.io-client@^4.8.1', error.message)
      return null
    }
  }

  const io = ioFactory

  if (socket?.connected) {
    console.log('🔌 [WS] Already connected, reusing existing connection')
    return socket
  }

  if (isConnecting) {
    console.log('🔌 [WS] Connection in progress, waiting...')
    return null
  }
  
  // If socket exists but not connected, clean it up first
  if (socket && !socket.connected) {
    console.log('🔌 [WS] Cleaning up disconnected socket before reconnecting')
    try {
      socket.removeAllListeners()
      socket.disconnect()
    } catch (e) {
      // Ignore cleanup errors
    }
    socket = null
  }

  if (!userId || !token) {
    console.warn('⚠️ [WS] No userId or token provided, cannot connect', { hasUserId: !!userId, hasToken: !!token })
    return null
  }

  isConnecting = true

  try {
    // Get WebSocket URL from API URL
    // Priority: window.HMI_API_BASE > REACT_APP_API_URL > fallback logic
    let apiUrl = null
    
    // First, check if window.HMI_API_BASE is set (for local development)
    if (window.HMI_API_BASE) {
      // Remove /api suffix if present to get base URL
      apiUrl = window.HMI_API_BASE.replace(/\/api\/?$/, '')
      console.log('🔌 [WS] Using window.HMI_API_BASE:', window.HMI_API_BASE, '-> base URL:', apiUrl)
    } else if (process.env.REACT_APP_API_URL) {
      // Check if REACT_APP_API_URL is set and is a full URL
      apiUrl = process.env.REACT_APP_API_URL
      // Remove /api suffix if present
      apiUrl = apiUrl.replace(/\/api\/?$/, '')
    }
    
    // If not set or relative path, try to determine API hostname
    if (!apiUrl || apiUrl === '/api' || apiUrl.startsWith('/')) {
      // If web app is on heresmyinfo.com, API is at api.heresmyinfo.com
      // Otherwise, use current origin
      if (window.location.hostname === 'heresmyinfo.com' || window.location.hostname.includes('heresmyinfo.com')) {
        apiUrl = 'https://api.heresmyinfo.com'
      } else {
        // For local development, default to production API if not running local API
        apiUrl = 'https://api.heresmyinfo.com'
      }
    }
    
    // Extract hostname and port from API URL
    const urlMatch = apiUrl.match(/^(https?:\/\/)?([^/]+)/)
    const wsHost = urlMatch ? urlMatch[2] : apiUrl.replace(/^https?:\/\//, '').split('/')[0]
    const protocol = apiUrl.startsWith('https') || window.location.protocol === 'https:' ? 'wss' : 'ws'
    const fullWsUrl = `${protocol}://${wsHost}`

    console.log(`🔌 [WS] Connecting to ${fullWsUrl}...`, { userId, hasToken: !!token })

    socket = io(fullWsUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
      reconnectionDelay: 1000,
      auth: {
        token,
        userId
      }
    })

    // Connection events
    socket.on('connect', () => {
      console.log('✅ [WS] Connected:', socket?.id)
      reconnectAttempts = 0
      isConnecting = false

      // Authenticate with server
      const authData = { userId, token }
      console.log('🔐 [WS] Authenticating with server:', { userId, hasToken: !!token })
      if (socket) {
        socket.emit('authenticate', authData)
      }
      // Don't trigger refresh on connect - only refresh on invalidate signals
      // This prevents infinite loops where refresh causes reconnection
    })

    socket.on('hello', (data) => {
      console.log('👋 [WS] Server hello:', data)
      // Don't trigger refresh on hello - only refresh on invalidate signals
      // This prevents infinite loops where refresh causes reconnection
    })

    socket.on('disconnect', (reason) => {
      console.log('🔌 [WS] Disconnected:', reason)
      isConnecting = false

      if (reason === 'io server disconnect' && socket) {
        // Server disconnected, reconnect manually
        socket.connect()
      }
    })

    socket.on('connect_error', (error) => {
      console.error('❌ [WS] Connection error:', error.message)
      isConnecting = false
      reconnectAttempts++

      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.warn('⚠️ [WS] Max reconnection attempts reached, giving up')
        disconnectWebSocket()
      }
    })

    socket.on('error', (error) => {
      console.error('❌ [WS] Socket error:', error)
    })

    // Listen for invalidate signals
    socket.on('invalidate', (payload) => {
      console.log('🔔 [WS] Invalidate signal received:', payload)
      onInvalidate(payload)
    })

    // Reconnection events
    socket.on('reconnect', (attemptNumber) => {
      console.log(`🔄 [WS] Reconnected after ${attemptNumber} attempts`)
      reconnectAttempts = 0

      // Re-authenticate after reconnect
      if (socket) {
        socket.emit('authenticate', { userId, token })
      }
    })

    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 [WS] Reconnection attempt ${attemptNumber}/${MAX_RECONNECT_ATTEMPTS}`)
    })

    socket.on('reconnect_failed', () => {
      console.error('❌ [WS] Reconnection failed')
      isConnecting = false
    })

    return socket
  } catch (error) {
    console.error('❌ [WS] Failed to connect:', error)
    isConnecting = false
    return null
  }
}

/**
 * Disconnect from WebSocket server
 */
export function disconnectWebSocket () {
  if (socket) {
    console.log('🔌 [WS] Disconnecting...')
    try {
      socket.removeAllListeners() // Remove all listeners to prevent memory leaks
      socket.disconnect()
    } catch (e) {
      console.warn('⚠️ [WS] Error during disconnect:', e.message)
    }
    socket = null
    isConnecting = false
    reconnectAttempts = 0
  }
}

/**
 * Check if WebSocket is connected
 */
export function isWebSocketConnected () {
  return socket?.connected || false
}

/**
 * Get WebSocket instance (for debugging)
 */
export function getWebSocket () {
  return socket
}

/**
 * Get WebSocket connection status (for debugging)
 */
export function getWebSocketStatus () {
  if (!socket) {
    return { connected: false, status: 'not_initialized' };
  }
  return {
    connected: socket.connected,
    id: socket.id,
    status: socket.connected ? 'connected' : 'disconnected',
    userId: socket.userId || 'unknown'
  };
}

